/**
 * O manual das telas que NÃO são recursos de cadastro.
 *
 * As outras dezenove têm o manual no catálogo da API, ao lado da definição do
 * recurso. Estas cinco são tarefas, não entidades — e por isso o texto delas
 * mora aqui, no módulo vizinho de onde o destino é declarado. A regra é a
 * mesma nos dois lugares: **o manual fica junto da coisa que ele descreve.**
 *
 * `Destino.ajuda` é obrigatório, então acrescentar uma tela sem escrever o
 * verbete dela não compila.
 *
 * **Por que num arquivo só dele, e não dentro de `App.tsx`.** O `App.tsx` é
 * JSX, e o Node executa TypeScript com `--experimental-strip-types`, que não
 * analisa JSX. Enquanto o manual morava lá, nenhum teste conseguia abri-lo —
 * e um manual que ninguém verifica é o manual que envelhece calado, que é
 * exatamente o defeito que esta regra existe para evitar. Aqui ele é dado
 * comum, e `ajudaDasTarefas.test.ts` o encara.
 */

import type { Ajuda as TextoDeAjuda } from './api'

export const AJUDA_DAS_TAREFAS: Record<string, TextoDeAjuda> = {
  grade: {
    paraQue:
      'A grade montada, e o que falta para montá-la. É a tela de trabalho: daqui se manda gerar, se confere o resultado e se fixa o que não deve mudar.',
    comoUsar: [
      'Escolha o cenário e a turma que você quer ver.',
      'Se faltar cadastro, a tela diz exatamente o que falta antes de deixar gerar.',
      'Escolha a ênfase — ela diz o que o sistema deve priorizar quando não dá para agradar a todos.',
      'Mande gerar e acompanhe; a grade aparece quando o motor termina.',
      'Clique numa aula para movê-la. Cada horário da turma aparece já marcado — livre, com aviso, ou ocupado —, e a aula movida fica travada: a próxima rodada não a tira do lugar. No mesmo painel dá para travar ou destravar sem mover.',
      'Gostou de parte da grade? Use "Rodar de novo…": marque o que fica congelado — por turma, disciplina, professor ou dia — e o que, mesmo assim, fica livre. A prévia diz quantas aulas ficam paradas antes de você rodar.',
      'A rodada vira um cenário novo, com a frase do que foi congelado e a partir de qual cenário. O original fica como estava, para comparar.',
      'A rodada pode ter um objetivo: "melhorar um critério sem piorar o resto". O motor trava a soma de todos os outros no valor da grade atual e só mexe para melhorar aquele — o "retirar mais janelas" do URÂNIA, para qualquer critério.',
      '"Baixar planilha" entrega a grade no formato de publicação — dia, tempo e horário de relógio nas linhas, uma coluna por turma, a disciplina com o professor em cada célula — e mais quatro abas: a grade de cada professor, a lista de disciplinas com os horários (a de eletivas sai dela com um filtro) quantas aulas acontecem ao mesmo tempo em cada horário, que é quantas salas são precisas; e os dias que cada professor liberou e ficaram sem aula — para conversar com ele, sem apagar nada do cadastro.',
      'Vendo a grade por professor, "Baixar a grade deste professor" entrega o arquivo dele sozinho, para mandar por e-mail.',
    ],
    cuidados: [
      'A conferência roda antes de gerar e aponta o que torna a grade impossível: turma que pede mais aula do que o turno dela tem horário, e professor com aula em horário que ele não liberou. São contas, não palpites — se aparecerem, a grade não fecharia mesmo.',
      'As marcas sobre as aulas apontam o que foi violado e com que peso. Grade sem marca nenhuma é raridade, não meta: o normal é escolher quais incômodos valem a pena.',
      'Aula pendente é aula que não achou lugar. A tela explica por quê e o que relaxar para ela caber.',
      'Gerar de novo não dá a mesma grade: o motor busca em paralelo. O que se compara entre rodadas é a qualidade, não célula a célula.',
      'Numa rodada, o que não foi congelado parte da grade anterior, mas pode mudar. Para garantir que algo não se mexa, congele — "parecido com antes" não é promessa.',
      'O que já tinha sido travado à mão continua travado em toda rodada, mesmo que o filtro diga para deixar livre.',
      'Horário "com aviso" — o professor não liberou, ou a aula não pode ali — aceita a aula se você disser o motivo. Ela vai "na cesta": fica marcada na grade, abre uma pendência em negociação, e a proibição se abre só naquele horário para a próxima rodada não ficar impossível. Horário "ocupado" não aceita: a turma ou o professor já está em outra aula.',
      '"As regras desta grade" lista tudo que o motor teve de cumprir ao montá-la, agrupado. Quando uma aula está num lugar estranho, comece pelos horários proibidos: os marcados "deduzida" o sistema tirou do turno da disciplina, do dia fixo ou de uma observação da planilha — ninguém os digitou, e são os que mais vale conferir.',
    ],
  },
  comparar: {
    paraQue:
      'Duas calibrações da mesma escola, lado a lado. Serve para decidir qual incômodo a instituição prefere ter.',
    comoUsar: [
      'Crie um segundo cenário com outra ênfase — por exemplo, um priorizando a janela do professor e outro a sala.',
      'Gere os dois.',
      'Abra esta tela e compare o custo de cada critério nos dois.',
    ],
    cuidados: [
      'Comparar só faz sentido entre cenários da mesma escola e do mesmo cadastro. Se você mudou a grade curricular entre as duas rodadas, está comparando duas perguntas diferentes.',
    ],
  },
  pendencias: {
    paraQue:
      'O que impede a grade de fechar, e o que foi feito com cada coisa. Substitui a planilha onde se cola o diagnóstico e se anota, ao lado, "e-mail enviado" ou "aguardando o professor".',
    comoUsar: [
      'Escolha o cenário. O quadro mostra os conflitos de cadastro — professor com aula num dia que não liberou, turma que pede mais aula do que tem horário — e as aulas que o motor não encaixou nesta grade.',
      'Abra uma pendência, diga quem cuida dela e em que pé está: em negociação, aguardando, resolvida.',
      'Anote o que foi feito. As notas ficam na pendência e atravessam as rodadas: se ela voltar, volta com o histórico.',
      'Para uma aula sem lugar, a pendência mostra quem poderia dá-la: os horários vagos da turma e, para cada professor do mesmo curso e série, se ele liberou aquele horário.',
      'Ou peça a sugestão ao motor: ele procura a menor mudança que faz a aula caber — mover outra aula, trocar o professor dentro do grupo — e diz em frases o que mudaria. O resultado é um cenário novo; o original não é tocado.',
    ],
    cuidados: [
      'Pendência que deixa de aparecer não vira resolvida sozinha. Ela fica no fim da lista, marcada, até alguém confirmar — sumir pode ser "o professor liberou o dia" e pode ser "alguém apagou a oferta".',
      'Pendência dada por resolvida que volta a aparecer é reaberta, e o histórico diz quando isso aconteceu.',
      'Aula sem lugar é de uma grade só: olhar outro cenário não a faz sumir.',
      'Quem é elegível segue a regra da escola: quem já dá aula naquele curso e naquela série, mais quem já está na oferta.',
      'A tabela de elegíveis mostra disponibilidade, não a forma da semana: pode acontecer de todos "poderem" e a aula mesmo assim não caber, por causa do Tipo da disciplina. Para isso serve a sugestão — ela é um cálculo de verdade, não uma consulta.',
      'Se a sugestão disser que não há mudança que resolva, é prova, não palpite: é caso de negociar com o professor ou com a coordenação. Se ela encaixar uma aula tirando outra do lugar, isso aparece primeiro.',
    ],
    tour: [
      {
        marca: 'estados',
        texto:
          'Quantas pendências há em cada estado. É o número que se acompanha de uma semana para a outra — o que antes era o nome da aba da planilha, "83" e depois "26".',
      },
      {
        marca: 'quadro',
        texto:
          'Cada linha diz o que não fecha, quem cuida e a última coisa anotada. Abra uma para mudar o estado, anotar e ver quem poderia dar a aula.',
      },
    ],
  },
  custo: {
    paraQue:
      'O que a folha de aula custa por mês: o planejado, assim que a atribuição existe, e o da grade montada, quando ela existe. É a resposta para "a sua grade está te dando isso de custo" — enquanto você monta, e não no fechamento da folha.',
    comoUsar: [
      'Escolha o cenário. Sem grade montada, o número é o planejado: quem a coordenação decidiu que dá cada aula.',
      'Com a grade montada, o detalhe passa a ser o da grade — quem de fato está em cada aula — e o planejado fica ao lado, com a diferença.',
      'Leia a janela primeiro. É a única linha que montar melhor diminui.',
      'Use o recorte por classificação para ver onde o dinheiro está por nível de carreira.',
      'A tabela por professor vai do mais caro para o menos.',
    ],
    cuidados: [
      'Professor que dá aula e está sem valor de hora-aula fica FORA do total, num bloco à parte. Somá-lo como zero diria que a grade é mais barata do que é — a direção errada para errar quando alguém decide contratação com este número.',
      'Sem grade montada, "janela: R$ 0" não quer dizer grade boa: quer dizer que ninguém sabe ainda. A tela avisa qual dos dois é.',
      'Aula que ficou sem lugar não é paga, então uma grade incompleta parece mais barata do que o planejado. Ela não é: está incompleta. A tela diz quantas aulas ficaram de fora antes de mostrar a diferença.',
      'É custo de aula em sala. Adicional de regime, adicional noturno e outras verbas entram na folha e não neste número.',
      'O valor da hora-aula vem da classificação do professor. Quem recebe diferente do nível declara no próprio cadastro, e é esse que vale.',
    ],
    // Sem passo para o bloco de "sem valor": ele só existe quando há
    // pendência, e um passo apontando para o que não está na tela deixaria o
    // tour falando sozinho.
    tour: [
      {
        marca: 'totais',
        texto:
          'A janela vem primeiro apesar de ser cerca de 1% do dinheiro: o total é inevitável, são os professores que a escola tem, e a janela é a única parte que montar melhor diminui.',
      },
      {
        marca: 'por-classificacao',
        texto:
          'Onde o dinheiro está, por nível de carreira. É o recorte que a diretoria pede — e o que hoje não aparece no resumo que ela vê.',
      },
      {
        marca: 'por-professor',
        texto:
          'Do mais caro para o menos. A coluna de janela só ganha número depois que a grade existe, porque janela é buraco entre aulas e sem horário não há entre.',
      },
    ],
  },
  escola: {
    paraQue:
      'O que esta escola é, quem entra nela, e como ela acaba. Abre clicando no nome da escola, na barra de cima.',
    comoUsar: [
      'Corrija o nome e o ano letivo. O nome é o que aparece na barra e na lista de troca de escola — é por ele que as pessoas acham a escola certa.',
      'Ajuste as semanas no mês se a sua folha contar diferente de 4,5. Esse número multiplica tudo na tela de Custo.',
      'Dê acesso por e-mail. Quem já tem conta no Pauta entra na hora; quem não tem recebe um link de convite.',
      'Tire quem saiu da equipe. O último proprietário não pode ser removido.',
    ],
    cuidados: [
      'Só proprietário altera, convida e exclui. Editor e leitor veem esta tela para saber a quem pedir, com os campos desabilitados.',
      'O link do convite aparece UMA vez. O servidor guarda só um resumo dele, como faz com as sessões — quem fechar a tela sem copiar precisa emitir outro.',
      'Excluir a escola a tira da vista de todos os membros de uma vez, e não há botão de desfazer aqui. O registro continua no banco com a hora da exclusão, porque o histórico das rodadas do motor depende dele, mas recuperar exige quem administra o servidor.',
      'Duas escolas com o mesmo nome são o jeito mais fácil de trabalhar horas na errada. Se existir uma repetida, renomeie uma das duas antes de qualquer outra coisa.',
    ],
    tour: [
      {
        marca: 'identificacao',
        texto:
          'O nome vive aqui e aparece em todo lugar. Foi a falta desta caixa que deixou duas escolas chamadas ESPM convivendo, uma cheia e uma vazia.',
      },
      {
        marca: 'membros',
        texto:
          'Quem participa, e com que papel. Leitor vê, editor trabalha, proprietário também convida e exclui.',
      },
      {
        marca: 'excluir',
        texto:
          'Pede o nome digitado de propósito: a exclusão é silenciosa — a escola apenas some da lista — e não há caminho de volta pela interface.',
      },
    ],
  },
  importar: {
    paraQue:
      'Subir planilha em vez de digitar. Lê a disponibilidade que veio do questionário e a planilha de atribuição de aulas, com os defeitos que elas realmente têm.',
    comoUsar: [
      'Escolha o arquivo e mande conferir. Nada é gravado nesse passo.',
      'Leia o retrato: quantas linhas entenderam, quem casou com o cadastro, e o que precisa da sua decisão.',
      'Se estiver certo, mande gravar.',
      'Para a planilha de atribuição, diga também em que unidade, turno e esquema as turmas entram — isso a planilha não carrega.',
    ],
    cuidados: [
      'Subir de novo é seguro e é o fluxo normal: o que existe é atualizado, o que falta nasce, e nada duplica.',
      'Professor cujo nome não casa com o cadastro fica de fora e aparece na lista. Matrícula preenchida no cadastro resolve a maioria desses casos.',
      'A conferência aponta a aba que ela escolheu. Confira: uma pasta com quatorze abas pode ter mais de uma parecida com demanda.',
      'Quem respondeu o questionário sem marcar nada não fica indisponível a semana toda — esse caso é apontado para você decidir, nunca adivinhado.',
    ],
  },
  formularios: {
    paraQue:
      'Cada professor preenche a própria disponibilidade por um link, sabendo o que precisa cobrir — e sem saber de que disciplina ou turma, porque isso a coordenação ainda não pode contar.',
    comoUsar: [
      'Antes do primeiro envio, abra "Ajustes do formulário": o tamanho do quadradinho (na ESPM, o tempo de 1h40, dois horários seguidos), o prazo em dias e o texto do convite que os professores já conhecem.',
      'Marque os professores e use "Enviar convite por e-mail": o sistema gera o link de cada um e manda o convite com link e prazo. O aviso no alto da tela diz para onde os e-mails vão — em modo de teste, só para a lista de teste, nunca para os professores.',
      'Para mandar à mão, gere os links. Cada um sai com o convite pronto, já com o link e o prazo dele: copie, ou abra direto no seu e-mail. "Copiar todos" sai em linhas de nome, e-mail, link e prazo, para mala direta.',
      'O professor abre o link e vê, por exemplo, "1 tempo no turno noite que só pode ser na quinta". Enquanto marca, o formulário diz se já cobre.',
      'A resposta entra direto na disponibilidade dele. Não precisa importar nada.',
      'Filtre "não cobrem a carga" para ver quem respondeu sem fechar, com a explicação de cada um. Cada caso desses também vira uma linha em Pendências.',
      'Passado o prazo, depois de achar as inconsistências e negociar, marque quem precisa ajustar e use "Reabrir para ajuste": o prazo recomeça no mesmo link, sem mandar outro.',
    ],
    cuidados: [
      'Em "Quem recebe os alertas", ponha os e-mails da equipe de horários e da coordenação: recebem o aviso de quem deixou o link vencer ou respondeu sem cobrir a carga.',
      'O link aparece uma vez só, na hora de gerar: o sistema guarda apenas uma impressão dele. Gerar outro para o mesmo professor invalida o anterior.',
      'Professor sem carga recebe o formulário aberto, sem números. Um "você tem 0 horas" contaria a ele o que ainda não pode ser contado.',
      'O formulário pede "pelo menos": liberar mais do que o pedido não é erro, é folga para montar a grade.',
      'A carga conta a grade curricular e o mínimo de cada professor nos grupos de atribuição. O máximo da cota não entra, porque não é certeza.',
      'Responder troca só os horários proibidos do professor. Uma preferência graduada à mão continua lá.',
    ],
    tour: [
      { marca: 'filtros', texto: 'Quem não cobre a carga, quem ainda não respondeu e quem nem recebeu link. O número ao lado é quantos há em cada um.' },
      { marca: 'lista', texto: 'Uma linha por professor: o que o formulário pede a ele, em que pé está o link e se a disponibilidade de hoje cobre o pedido.' },
      { marca: 'ajustes', texto: 'Quadradinho, prazo e convite: o que muda de escola para escola. Ajuste uma vez por semestre.' },
      { marca: 'gerar', texto: 'Marque os professores na lista e gere os links aqui, ou reabra o link que eles já têm.' },
    ],
  },
  menu: {
    paraQue:
      'Esconder do menu o que esta escola não usa. Ensino superior não tem hora-atividade nem jornada CLT; quem está num campus só não tem deslocamento.',
    comoUsar: [
      'Marque o que a escola usa e desmarque o resto.',
      'Grave. O menu se remonta sem recarregar a página.',
    ],
    cuidados: [
      'Desmarcar não apaga nada: o cadastro continua no banco, a tela continua alcançável e a geração continua levando tudo em conta. Some só a entrada do menu.',
      'O manual também encolhe: o que você esconder deixa de aparecer na Ajuda.',
    ],
  },
}
